import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, of, throwError, forkJoin } from 'rxjs';
import { finalize, take, switchMap, catchError, map, shareReplay } from 'rxjs/operators';

import { URLConstants } from '@app/core/constants/URLConstants';
import { APIWithLoaderHelper } from '../helpers/apiWithLoaderHelper';
import { LocationParamsService } from './locationParamsService';
import { MapLayersApi } from './types/MapLayersApi';
import { EnvironmentalDataSource } from './types/EnvironmentalDataSource';
import { GeoJsonObject } from 'geojson';

export type MapConfig = {
  center: number[];
  zoom: number;
};

/**
 * Service to interact with map-related APIs and manage map layer state.
 *
 * Provides methods to fetch map layers, environmental data sources, and layer GeoJSON data.
 * Indicates when background layer refresh/reorder operations are in progress.
 *
 * Caches in-flight requests for map layers to deduplicate concurrent calls for the same site ID.
 */
@Injectable({
  providedIn: 'root',
})
export class MapService {
  private reorderBusySubject = new BehaviorSubject<boolean>(false);
  private inFlightMapLayers = new Map<string, Observable<MapLayersApi[]>>();
  /**
   * Subject to notify when map layers are programmatically initializing for a site.
   */
  private layerInitSubject = new Subject<{ siteId: string; state: 'initializing' | 'initialized' }>();

  /**
   * Observable that emits when layers are being initialized or finished initializing for a site
   * Emits an object { siteId, state } where state is 'initializing' or 'initialized'.
   */
  public get layerInit$(): Observable<{ siteId: string; state: 'initializing' | 'initialized' }> {
    return this.layerInitSubject.asObservable();
  }

  /**
   * Notify listeners that layers for a site are initializing.
   */
  public notifyLayerInitializing(siteId: string): void {
    this.layerInitSubject.next({ siteId, state: 'initializing' });
  }

  /**
   * Notify listeners that layers for a site have finished initializing.
   */
  public notifyLayerInitialized(siteId: string): void {
    this.layerInitSubject.next({ siteId, state: 'initialized' });
  }

  /**
   * Set whether a reorder/background refresh is in progress.
   */
  public setReorderBusy(value: boolean): void {
    this.reorderBusySubject.next(!!value);
  }

  /**
   * Observable exposing whether reorder/refresh is in progress.
   */
  public get reorderBusy$(): Observable<boolean> {
    return this.reorderBusySubject.asObservable();
  }

  constructor(
    private httpService: HttpClient,
    private apiWithLoaderHelper: APIWithLoaderHelper,
    private locationParamsService: LocationParamsService
  ) {}

  /**
   * Fetch map layers for a given site ID.
   *
   * Notifies listeners when layer initialization starts and finishes.
   *
   * Will return a cached in-flight observable if a request for the same siteId is already in progress.
   * Shares the single request among multiple subscribers. so late subscribers get the last value.
   *
   * @param siteId The ID of the site to fetch map layers for.
   * @returns An Observable emitting an array of MapLayersApi objects.
   */
  public getMapLayers(siteId: string): Observable<MapLayersApi[]> {
    const cached = this.inFlightMapLayers.get(siteId);
    if (cached) return cached;

    try {
      this.notifyLayerInitializing(siteId);
    } catch (e) {}

    const observable$ = this.apiWithLoaderHelper
      .get<MapLayersApi[]>(URLConstants.getSiteMapLayers(siteId), {}, false)
      .pipe(
        finalize(() => {
          try {
            this.notifyLayerInitialized(siteId);
          } catch (e) {}
          this.inFlightMapLayers.delete(siteId);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.inFlightMapLayers.set(siteId, observable$);
    return observable$;
  }

  public getMapEnvironmentDataSources(siteId: string): Observable<EnvironmentalDataSource[]> {
    return this.apiWithLoaderHelper.get<EnvironmentalDataSource[]>(
      URLConstants.getEnvironmentalDataSources(siteId),
      {},
      false
    );
  }

  /**
   * Fetch GeoJSON and optional SLD for a map layer by its ID (and optional site ID).
   * Returns an object containing the MapLayersApi info plus `data` (the GeoJSON or null if not found)
   * and `sld` (the SLD string or undefined if not found or not applicable).
   * If fetching the GeoJSON or SLD fails with a 404, treats as missing data and returns null/undefined.
   * Other errors are propagated to the caller.
   *
   * @param layerId The ID of the map layer to fetch.
   * @param siteId Optional site ID if the layer is site-specific.
   * @returns An Observable emitting the MapLayersApi object with added `data` and `sld` properties.
   */
  public getMapLayerJSON(
    layerId: string,
    siteId?: string
  ): Observable<MapLayersApi & { data?: GeoJsonObject | null; sld?: string; fetchError?: any }> {
    return this.apiWithLoaderHelper
      .get<MapLayersApi>(URLConstants.getSiteLayerFile(siteId as string, layerId), {}, true)
      .pipe(
        take(1),
        switchMap((fullLayer) => {
          const data$ = this.httpService.get<GeoJsonObject>(fullLayer.dataUrl).pipe(
            take(1),
            catchError((err) => {
              if (err && (err.status === 404 || err.status === '404')) {
                console.warn('Map layer GeoJSON not found (404)', { layerId, siteId, url: fullLayer.dataUrl });
                return of(null);
              }
              return throwError(() => err);
            })
          );

          const sld$ = fullLayer.sldUrl
            ? this.httpService.get(fullLayer.sldUrl, { responseType: 'text' }).pipe(
                take(1),
                catchError((err) => {
                  if (err && (err.status === 404 || err.status === '404')) {
                    console.warn('Map layer SLD not found (404)', { layerId, siteId, url: fullLayer.sldUrl });
                    return of(undefined);
                  }
                  return throwError(() => err);
                })
              )
            : of(undefined);

          return forkJoin([data$, sld$]).pipe(map(([data, sld]) => ({ ...fullLayer, data: data ?? null, sld })));
        })
      );
  }

  /**
   * Run a Promise-returning work function while emitting layer initialization events for a site.
   * Ensures notifyLayerInitializing is emitted before the work starts and notifyLayerInitialized
   * is emitted after the work settles (either resolves or rejects).
   *
   * Catches and ignores errors from the notify calls to avoid interfering with the work.
   *
   * @param siteId The site ID for which layers are being initialized.
   * @param work A function returning a Promise to execute.
   * @returns The Promise returned by the work function.
   */
  public runWithLayerInit<T>(siteId: string, work: () => Promise<T>): Promise<T> {
    try {
      this.notifyLayerInitializing(siteId);
    } catch (e) {}
    return work()
      .then((res) => {
        try {
          this.notifyLayerInitialized(siteId);
        } catch (e) {}
        return res;
      })
      .catch((err) => {
        try {
          this.notifyLayerInitialized(siteId);
        } catch (e) {}
        throw err;
      });
  }

  /**
   * Persist a custom order for map layers for the current site in localStorage.
   * Order is stored as array of layer ids (strings like '123').
   * If no siteId is provided or an error occurs, nothing is saved.
   */
  public saveCustomMapLayerOrderForSite(siteId: string | null, orderKeys: string[]): void {
    if (!siteId) return;
    try {
      const key = `mapLayerOrder:${siteId}`;
      const store = orderKeys.map((k) => k.replace('map-layer-', ''));
      localStorage.setItem(key, JSON.stringify(store));
    } catch (e) {
      console.error('Error saving custom map layer order', e);
    }
  }

  /**
   * Load a previously saved custom order for a site (returns array of keys like 'map-layer-123')
   */
  public loadCustomMapLayerOrderForSite(siteId: string | null): string[] | null {
    if (!siteId) return null;
    try {
      const key = `mapLayerOrder:${siteId}`;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const ids: string[] = JSON.parse(raw);
      return ids.map((id) => `map-layer-${id}`);
    } catch (e) {
      console.error('Error loading custom map layer order', e);
      return null;
    }
  }

  /**
   * Persist the currently selected map layer keys for the given site to localStorage.
   * Stores as an array of numeric ids (without the 'map-layer-' prefix) at key `mapLayerSelection:{siteId}`.
   */
  public saveMapLayerSelectionForSite(siteId: string | null, keys: string[]): void {
    if (!siteId) return;
    try {
      const storeKey = `mapLayerSelection:${siteId}`;
      const store = keys.map((k) => k.replace('map-layer-', ''));
      localStorage.setItem(storeKey, JSON.stringify(store));
    } catch (e) {
      console.error('Error saving map layer selection', e);
    }
  }

  /**
   * Persist the currently selected map group keys for the given site to localStorage.
   * Stores as an array of keys at key `mapGroupSelection:{siteId}`.
   */
  public saveMapGroupSelectionToLocalStorage(siteId: string | null, keys: string[]): void {
    if (!siteId) return;
    try {
      const storeKey = `mapGroupSelection:${siteId}`;
      localStorage.setItem(storeKey, JSON.stringify(keys));
    } catch (e) {
      console.error('Error saving map layer selection', e);
    }
  }

  /**
   * Load previously saved map layer selection for the given site from localStorage.
   * Returns an array of keys like 'map-layer-123' or null if none.
   */
  public loadMapLayerSelectionForSite(siteId: string | null): string[] | null {
    if (!siteId) return null;
    try {
      const storeKey = `mapLayerSelection:${siteId}`;
      const raw = localStorage.getItem(storeKey);
      if (!raw) return null;
      const ids: string[] = JSON.parse(raw);
      return ids.map((id) => `map-layer-${id}`);
    } catch (e) {
      console.error('Error loading map layer selection', e);
      return null;
    }
  }

  /**
   * Load previously saved map group selection for the given site from localStorage.
   * Returns an array of group keys or null if none.
   */
  public loadMapGroupSelectionFromLocalStorage(siteId: string | null): string[] | null {
    if (!siteId) return null;
    try {
      const storeKey = `mapGroupSelection:${siteId}`;
      const raw = localStorage.getItem(storeKey);
      if (!raw) return null;
      const keys: string[] = JSON.parse(raw);
      return keys;
    } catch (e) {
      console.error('Error loading map group selection', e);
      return null;
    }
  }
}
